import Swal from "sweetalert2";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Row, Col, Button, Spinner, Badge } from "reactstrap";
import { Block, Icon } from "../../../components/Component";
import { Popover, Table, TableColumnsType } from "antd";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import MaterialModal from "./MaterialModal";
import supplierApis from "../../../api/master/supplier";
import boqApis from "../../../api/boq/boq";
import BulkMaterialUpload from "./BulkMaterialUpload";
import { groupMaterialsBySiteStructure, validateSiteStructureGrouping } from "../../../utils/Utils";
const WORK_LEVEL_LABELS: Record<string, string> = {
  TOWER_BLOCK: "Tower / Block",
  VILLA: "Villa",
  FLOOR: "Floor",
  FLAT: "Flat",
};

const HIERARCHY_UNIT_TITLES: Record<string, string> = {
  TOWER_BLOCK: "Tower / Block",
  VILLA: "Villa",
  FLOOR: "Tower / Block",
  FLAT: "Tower / Block",
};

const PurchaseRequest_Materails: React.FC = (props: any) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    getValues,
  } = useFormContext();
  const siteIdWatch = watch("siteId");
  const contractorIdWatch = watch("contractorId");
  const approvalRequired = watch("approvalRequired");
  const approvalsData = watch("approvalsData");
  const isFinalApprover = watch("isFinalApprover");

  const { data: currentUser }: any = useSelector(
    (state: RootState) => state.currentUser
  );
  const isAdmin = currentUser?.genericRole?.toLowerCase() === "admin";
  const canBypassApproval = isFinalApprover === true || isAdmin;

  const isApprovalRequiredYes = approvalRequired === "true";

  const hasApprovalsSet = useMemo(() => {
    try {
      const parsed =
        typeof approvalsData === "string"
          ? JSON.parse(approvalsData || "{}")
          : approvalsData || {};
      return Array.isArray(parsed?.data) && (parsed.data?.length ?? 0) > 0;
    } catch {
      return false;
    }
  }, [approvalsData]);
  const [materialCategoryOptions, setMaterialCategoryOptions] = useState([]);
  const [mobileView, setMobileView] = useState(false);
  const [sm, setSm] = useState(false);
  const [screenSize, setScreenSize] = useState(0);

  const [activeMaterial, setActiveMaterial] = useState<any>({
    action: null,
    index: null,
    item: null,
  });
  const [boqData, setBoqData] = useState<any[]>([]);

  const { fields, append, remove, update, replace }: any = useFieldArray({
    control,
    name: "items",
  });

  // Fetch BOQ data for PR remaining quantity validation
  useEffect(() => {
    if (siteIdWatch && contractorIdWatch) {
      (async () => {
        try {
          const response = await boqApis.fetchContractorBoqDropdown({
            siteIds: siteIdWatch.toString(),
            contractorIds: contractorIdWatch.toString(),
          });
          const data = response?.data || response?.response || [];
          setBoqData(Array.isArray(data) ? data : []);
        } catch (error) {
          console.log("Failed to fetch BOQ data:", error);
          setBoqData([]);
        }
      })();
    } else {
      setBoqData([]);
    }
  }, [siteIdWatch, contractorIdWatch]);

  // Compute exceeded materials for inline error display
  const exceededMaterials = useMemo(() => {
    if (!boqData.length || !fields?.length) return [];
    const exceeded: { label: string; enteredQty: number; remainingQty: number }[] = [];
    fields.forEach((item: any) => {
      const matched = boqData.find(
        (boq: any) =>
          (boq.materialId?.toString() || boq.materialID?.toString()) === item.materialName?.toString()
      );
      if (matched) {
        const enteredQty = parseFloat(item.quantity) || 0;
        const prRemainingQty = parseFloat(matched.prRemainingQty) || 0;
        if (enteredQty > prRemainingQty) {
          exceeded.push({
            label: item.materialLabel || item.categoryLabel || item.materialName,
            enteredQty,
            remainingQty: prRemainingQty,
          });
        }
      }
    });
    return exceeded;
  }, [boqData, fields]);

  const shouldBlockNext =
    exceededMaterials.length > 0 &&
    (approvalRequired !== "true" || !hasApprovalsSet) &&
    !canBypassApproval;

  const workLevelSettingWatch = watch("workLevelSetting");
  const previousFieldsRef = useRef<any[]>([]);

  const hierarchyUnitColumnTitle = useMemo(() => {
    const level = workLevelSettingWatch;
    if (!level || level === "NONE") {
      return "Tower / Block / Villa";
    }
    return HIERARCHY_UNIT_TITLES[level] ?? "Tower / Block";
  }, [workLevelSettingWatch]);

  const addNewRow = () => {
    setActiveMaterial({
      action: "add",
      index: null,
      item: null,
    });
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  const hierarchyVis = useMemo(() => {
    if (!fields?.length) {
      return {
        segmentLevel: false,
        tower: false,
        floor: false,
        flat: false,
        location: false,
      };
    }
    console.log("fields", fields);
    return {
      segmentLevel: fields.some(
        (item: any) =>
          item?.segmentLevel &&
          item.segmentLevel.toString().toLowerCase() !== "null",
      ),
      tower: fields.some(
        (item: any) =>
          item?.towerBlockVillaId ||
          item?.towerBlockVillaLabel ||
          item?.towerBlockVillaName ||
          item?.siteStructureUnitId ||
          item?.unitName,
      ),
      floor: fields.some(
        (item: any) => item?.floorNo != null && item?.floorNo !== "",
      ),
      flat: fields.some(
        (item: any) =>
          item?.flatId ||
          item?.flatLabel ||
          item?.flatName ||
          item?.siteStructureSubUnitId ||
          item?.subUnitName,
      ),
      location: fields.some(
        (item: any) =>
          item?.locationId || item?.locationLabel || item?.locationName,
      ),
    };
  }, [fields]);

  const columns: TableColumnsType = useMemo(() => {
    const baseColumns: TableColumnsType = [
      {
        title: "#",
        key: "index",
        width: 50,
        render: (text: any, record: any, index: number) => index + 1,
      },
      {
        title: "Material Category",
        dataIndex: "categoryLabel",
        key: "categoryLabel",
        width: 200,
        fixed: "left",
      },
      {
        title: "Material Name",
        dataIndex: "materialLabel",
        key: "materialLabel",
        width: 200,
        fixed: "left",
        render: (text, record) => (
          <div className="d-flex align-items-center justify-content-between pe-2">
            <span>{record.materialLabel}</span>
            {record?.errorFlag == true ? (
              <Popover
                placement="left"
                content={
                  <div>
                    <p>{record?.errorMessage?.join(", ")}</p>
                  </div>
                }
                title={<span style={{ color: "red" }}>Failed</span>}
              >
                <Badge
                  color="outline-danger"
                  placement="right"
                  style={{ cursor: "pointer" }}
                >
                  <span>Error</span>
                  <Icon name="info" />
                </Badge>
              </Popover>
            ) : (
              <></>
            )}
          </div>
        ),
      },
      {
        title: "Make",
        dataIndex: "makeInfo",
        key: "makeInfo",
        width: 180,
        render: (_: any, record: any) => {
          if (record?.makeInfo?.length > 0) {
            return record.makeInfo.map((m: any) => m.makeName).join(", ");
          }
          return "-";
        },
      },
      {
        title: "Specification",
        dataIndex: "specification",
        key: "specification",
        width: 200,
      },
      {
        title: "Req Qty",
        dataIndex: "quantity",
        key: "quantity",
        width: 150,
        render: (text, record) => `${text} ${record?.uomLabel}`,
      },
      {
        title: "Remarks",
        dataIndex: "remarks",
        key: "remarks",
        width: 150,
      },
    ];

    // Site Hierarchy column removed - not needed in materials table
    // if (hierarchyVis.segmentLevel) {
    //   baseColumns.push({
    //     title: "Site Hierarchy",
    //     dataIndex: "workLevelSetting",
    //     key: "siteHierarchyLevel",
    //     width: 150,
    //     render: (_: any, record: any) => {
    //       if (record?.key === "lastRow") {
    //         return "--";
    //       }
    //       const value = record?.workLevelSetting || record?.segmentLevel || "-";
    //       return WORK_LEVEL_LABELS[value] || value || "-";
    //     },
    //   });
    // }

    if (hierarchyVis.tower) {
      baseColumns.push({
        title: hierarchyUnitColumnTitle,
        dataIndex: "towerBlockVillaLabel",
        key: "towerBlockVilla",
        width: 180,
        render: (_: any, record: any) => {
          console.log("record", record);
          if (record?.key === "lastRow") {
            return "--";
          }
          return (
            record?.towerBlockVillaLabel ||
            record?.towerBlockVillaName ||
            record?.towerBlockName ||
            record?.unitName ||
            "-"
          );
        },
      });
    }

    if (hierarchyVis.floor) {
      baseColumns.push({
        title: "Floor",
        dataIndex: "floorNo",
        key: "floorNo",
        width: 120,
        render: (value: any, record: any) => {
          if (record?.key === "lastRow") {
            return "--";
          }
          return value ?? "-";
        },
      });
    }

    if (hierarchyVis.flat) {
      baseColumns.push({
        title: "Flat",
        dataIndex: "flatLabel",
        key: "flatName",
        width: 160,
        render: (_: any, record: any) => {
          if (record?.key === "lastRow") {
            return "--";
          }
          return (
            record?.flatLabel ||
            record?.flatName ||
            record?.siteStructureSubUnitName ||
            record?.subUnitName ||
            "-"
          );
        },
      });
    }

    if (hierarchyVis.location) {
      baseColumns.push({
        title: "Location",
        dataIndex: "locationLabel",
        key: "locationName",
        width: 180,
        render: (_: any, record: any) => {
          if (record?.key === "lastRow") {
            return "--";
          }
          return record?.locationLabel || record?.locationName || "-";
        },
      });
    }

    baseColumns.push({
      title: "Action",
      key: "operation",
      fixed: "right",
      width: 120,
      render: (text, record, index) => (
        <>
          <Button
            size="sm"
            color=""
            onClick={() => {
              setActiveMaterial({
                action: "edit",
                index: index,
                item: record,
              });
            }}
          >
            <Icon name="edit" />
          </Button>
          <Button
            size="sm"
            color=""
            onClick={() => {
              Swal.fire({
                title: `Are you sure, want to delete the material?`,
                text: `${
                  record?.materialLabel || "This Material"
                } will be deleted.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, delete it!",
              }).then((result) => {
                if (result.isConfirmed) {
                  remove(index);
                }
              });
            }}
          >
            <Icon name="trash" />
          </Button>
        </>
      ),
    });

    return baseColumns;
  }, [
    hierarchyVis,
    remove,
    setActiveMaterial,
    fields,
    hierarchyUnitColumnTitle,
  ]);

  const fetchCategorys = async (siteId?: string | number) => {
    try {
      const { data, status } = await supplierApis.fetchSupplierCategory(siteId);
      if (status) {
        const categoryOption = data.map((item: any) => ({
          label: item.category,
          value: item.id.toString(),
        }));
        setMaterialCategoryOptions(categoryOption);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Validation function to check hierarchy fields
  const validateHierarchyFields = (item: any, workLevelSetting: string) => {
    const errors: string[] = [];
    const itemWorkLevel = item?.workLevelSetting || workLevelSetting;

    if (!itemWorkLevel || itemWorkLevel === "NONE") {
      return { isValid: true, errors: [] };
    }

    // Helper function to check if a value is valid (not null, undefined, empty, "-", or "null")
    const isValidValue = (value: any): boolean => {
      if (!value) return false;
      const strValue = String(value).trim();
      return (
        strValue !== "" &&
        strValue !== "-" &&
        strValue !== "null" &&
        strValue !== "undefined"
      );
    };
console.log("itemWorkLevel",itemWorkLevel)
    // Check Tower/Block for TOWER_BLOCK
    // Priority: towerBlockVillaId must be present (not null/undefined/empty)
    // Label is only a fallback for display, but ID is the source of truth
    if (itemWorkLevel === "TOWER_BLOCK") {
      const hasTowerBlock =
        item?.towerBlockVillaId != null &&
        item?.towerBlockVillaId !== "" &&
        item?.towerBlockVillaId !== "null" &&
        item?.towerBlockVillaId !== "undefined" ||
        item?.siteStructureUnitId != null &&
        item?.siteStructureUnitId !== "" &&
        item?.siteStructureUnitId !== "null" &&
        item?.siteStructureUnitId !== "undefined";

      if (!hasTowerBlock) {
        errors.push("Tower/Block is required");
      }
    }

    // Check Villa for VILLA
    // Priority: towerBlockVillaId must be present (not null/undefined/empty)
    // Label is only a fallback for display, but ID is the source of truth
    if (itemWorkLevel === "VILLA") {
      const hasVilla =
        item?.towerBlockVillaId != null &&
        item?.towerBlockVillaId !== "" &&
        item?.towerBlockVillaId !== "null" &&
        item?.towerBlockVillaId !== "undefined" ||
        item?.siteStructureUnitId != null &&
        item?.siteStructureUnitId !== "" &&
        item?.siteStructureUnitId !== "null" &&
        item?.siteStructureUnitId !== "undefined";

      if (!hasVilla) {
        errors.push("Villa is required");
      }
    }

    // Check Tower/Block for FLOOR, FLAT (as prerequisite)
    // Priority: towerBlockVillaId must be present (not null/undefined/empty)
    // Label is only a fallback for display, but ID is the source of truth
    if (itemWorkLevel === "FLOOR" || itemWorkLevel === "FLAT") {
      const hasTowerBlock =
        item?.towerBlockVillaId != null &&
        item?.towerBlockVillaId !== "" &&
        item?.towerBlockVillaId !== "null" &&
        item?.towerBlockVillaId !== "undefined" ||
        item?.siteStructureUnitId != null &&
        item?.siteStructureUnitId !== "" &&
        item?.siteStructureUnitId !== "null" &&
        item?.siteStructureUnitId !== "undefined";

      if (!hasTowerBlock) {
        errors.push("Tower/Block is required");
      }
    }

    // Floor is now optional for all work levels (no validation required)

    // Check Flat for FLAT
    // if (itemWorkLevel === "FLAT") {
    //   const hasFlat =
    //     item?.flatId ||
    //     (item?.flatLabel &&
    //       item?.flatLabel !== "-" &&
    //       item?.flatLabel !== "null" &&
    //       String(item?.flatLabel).trim() !== "") ||
    //     (item?.flatName &&
    //       item?.flatName !== "-" &&
    //       item?.flatName !== "null" &&
    //       String(item?.flatName).trim() !== "");
    //   if (!hasFlat) {
    //     errors.push("Flat is required");
    //   }
    // }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  // Helper to check if error messages are from backend (not frontend validation)
  const hasBackendErrors = (errorMessage: any[]): boolean => {
    if (!errorMessage || !Array.isArray(errorMessage) || errorMessage.length === 0) {
      return false;
    }
    // Backend errors typically contain specific messages like "not found" or are not standard validation messages
    const frontendValidationMessages = [
      "Tower/Block is required",
      "Villa is required",
      "Floor is required",
      "Flat is required",
      "Location is required",
    ];
    // If any error message is NOT a frontend validation message, it's from backend
    return errorMessage.some(
      (msg) => !frontendValidationMessages.includes(msg)
    );
  };

  /**
   * Hierarchy-aware duplicate check for materials.
   *
   * Rules:
   * - VILLA/TOWER_BLOCK: duplicate if same materialName + same towerBlockVillaId
   * - FLOOR/FLAT without flat: duplicate if same materialName + tower + floor (any flat)
   * - FLOOR/FLAT with flat: duplicate if same materialName + tower + floor + flat,
   *   OR if same material already exists at floor-level (without flat)
   * - Also checks locationId
   */
  const checkHierarchyDuplicate = (
    data: any,
    existingItems: any[],
    excludeIndex?: number
  ): { isDuplicate: boolean; message: string } => {
    const currentWorkLevel =
      workLevelSettingWatch || getValues("workLevelSetting");

    const itemsToCheck = existingItems.filter(
      (_: any, idx: number) => idx !== excludeIndex
    );

    const hasFlat = (item: any) =>
      item?.flatId != null && item?.flatId !== "" && item?.flatId !== "null" && item?.flatId !== "undefined";

    for (const item of itemsToCheck) {
      if (item.materialName !== data.materialName) continue;
      if (item.locationId != data.locationId) continue;

      // VILLA / TOWER_BLOCK flow: same tower/villa = duplicate
      if (currentWorkLevel === "VILLA" || currentWorkLevel === "TOWER_BLOCK") {
        if (item.towerBlockVillaId == data.towerBlockVillaId) {
          return { isDuplicate: true, message: "Material Already Added" };
        }
        continue;
      }

      // FLOOR / FLAT flow
      if (currentWorkLevel === "FLOOR" || currentWorkLevel === "FLAT") {
        if (
          item.towerBlockVillaId != data.towerBlockVillaId ||
          item.floorNo != data.floorNo
        ) continue;

        const existingHasFlat = hasFlat(item);
        const newHasFlat = hasFlat(data);

        // New item has NO flat → applies to all flats in that floor
        if (!newHasFlat) {
          return {
            isDuplicate: true,
            message: existingHasFlat
              ? "Material already exists in a flat on this floor. Cannot add at floor level."
              : "Material Already Added",
          };
        }

        // New item HAS flat
        if (newHasFlat) {
          if (!existingHasFlat) {
            return {
              isDuplicate: true,
              message: "Material already added at floor level, which covers all flats.",
            };
          }
          if (item.flatId == data.flatId) {
            return { isDuplicate: true, message: "Material Already Added" };
          }
        }
      }
    }

    return { isDuplicate: false, message: "" };
  };

  // Validate items whenever workLevelSetting changes
  useEffect(() => {
    if (!fields || fields.length === 0) {
      return;
    }

    const currentWorkLevelSetting =
      workLevelSettingWatch || getValues("workLevelSetting");
    if (!currentWorkLevelSetting || currentWorkLevelSetting === "NONE") {
      // No hierarchy required, check if any items have error flags to clear
      // But preserve backend errors
      const hasErrors = fields.some((item: any) => item?.errorFlag);
      if (hasErrors) {
        const updatedFields = fields.map((item: any) => {
          // Preserve backend errors, only clear frontend validation errors
          if (hasBackendErrors(item?.errorMessage)) {
            return item; // Keep backend errors as is
          }
          return {
            ...item,
            errorFlag: false,
            errorMessage: [],
          };
        });
        replace(updatedFields);
      }
      return;
    }

    let hasChanges = false;
    const updatedFields = fields.map((item: any) => {
      // If item has backend errors, preserve them and don't overwrite
      if (hasBackendErrors(item?.errorMessage)) {
        return item;
      }

      const validation = validateHierarchyFields(item, currentWorkLevelSetting);
      const hasError = !validation.isValid;
      const errorMessages = validation.errors;

      // Only update if errorFlag or errorMessage changed
      const currentErrorFlag = item?.errorFlag || false;
      const currentErrorMessage = item?.errorMessage || [];
      const errorFlagChanged = currentErrorFlag !== hasError;
      const errorMessageChanged =
        JSON.stringify(currentErrorMessage) !== JSON.stringify(errorMessages);

      if (errorFlagChanged || errorMessageChanged) {
        hasChanges = true;
        return {
          ...item,
          errorFlag: hasError,
          errorMessage: errorMessages,
        };
      }
      return item;
    });

    if (hasChanges) {
      replace(updatedFields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workLevelSettingWatch]);

  // Validate items when fields are added/updated
  useEffect(() => {
    if (!fields || fields.length === 0) {
      previousFieldsRef.current = [];
      return;
    }

    const currentWorkLevelSetting =
      workLevelSettingWatch || getValues("workLevelSetting");

    // Check if fields actually changed (not just length, but content)
    const fieldsChanged =
      previousFieldsRef.current.length !== fields.length ||
      fields.some((item: any, index: number) => {
        if (item?.key === "lastRow") return false;
        const prevItem = previousFieldsRef.current[index];
        if (!prevItem) return true;
        return (
          prevItem?.towerBlockVillaId !== item?.towerBlockVillaId ||
          prevItem?.towerBlockVillaLabel !== item?.towerBlockVillaLabel ||
          prevItem?.floorNo !== item?.floorNo ||
          prevItem?.flatId !== item?.flatId ||
          prevItem?.workLevelSetting !== item?.workLevelSetting ||
          prevItem?.siteStructureUnitId !== item?.siteStructureUnitId ||
          prevItem?.siteStructureSubUnitId !== item?.siteStructureSubUnitId
        );
      });

    if (!fieldsChanged && previousFieldsRef.current.length > 0) {
      return;
    }

    previousFieldsRef.current = fields.map((item: any) => ({ ...item }));

    if (!currentWorkLevelSetting || currentWorkLevelSetting === "NONE") {
      // Clear errors if no hierarchy required, but preserve backend errors
      const hasErrors = fields.some((item: any) => item?.errorFlag);
      if (hasErrors) {
        const updatedFields = fields.map((item: any) => {
          // Preserve backend errors, only clear frontend validation errors
          if (hasBackendErrors(item?.errorMessage)) {
            return item; // Keep backend errors as is
          }
          return {
            ...item,
            errorFlag: false,
            errorMessage: [],
          };
        });
        replace(updatedFields);
        previousFieldsRef.current = updatedFields.map((item: any) => ({
          ...item,
        }));
      }
      return;
    }

    // Validate all items and update if needed
    let hasChanges = false;
    const updatedFields = fields.map((item: any) => {
      if (item?.key === "lastRow") return item;

      // If item has backend errors, preserve them and don't overwrite
      if (hasBackendErrors(item?.errorMessage)) {
        console.log("Preserving backend errors for item:", {
          materialLabel: item?.materialLabel,
          errorMessage: item?.errorMessage,
        });
        return item;
      }

      const validation = validateHierarchyFields(item, currentWorkLevelSetting);
      const expectedErrorFlag = !validation.isValid;
      const expectedErrors = validation.errors;
      const currentErrorFlag = item?.errorFlag || false;
      const currentErrors = item?.errorMessage || [];

      console.log("Validation result for item:", {
        materialLabel: item?.materialLabel,
        workLevelSetting: item?.workLevelSetting || currentWorkLevelSetting,
        towerBlockVillaId: item?.towerBlockVillaId,
        towerBlockVillaLabel: item?.towerBlockVillaLabel,
        siteStructureUnitId: item?.siteStructureUnitId,
        expectedErrorFlag,
        expectedErrors,
        currentErrorFlag,
        currentErrors,
      });

      if (
        currentErrorFlag !== expectedErrorFlag ||
        JSON.stringify(currentErrors) !== JSON.stringify(expectedErrors)
      ) {
        hasChanges = true;
        return {
          ...item,
          errorFlag: expectedErrorFlag,
          errorMessage: expectedErrors,
        };
      }
      return item;
    });

    if (hasChanges) {
      replace(updatedFields);
      previousFieldsRef.current = updatedFields.map((item: any) => ({
        ...item,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, workLevelSettingWatch]);

  useEffect(() => {
    fetchCategorys(siteIdWatch);
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, [siteIdWatch]);

  return (
    <>
      <Block>
        <form className="form-validate" onSubmit={handleSubmit(() => {})}>
          <div className="d-flex justify-content-end">
            <BulkMaterialUpload
              moduleKey={"add-new-purchase-request"}
              append={append}
              extra={{ fields }}
              selectedSiteId={watch("siteId")}
            />
          </div>
          <div className="table-responsive mt-2">
            <div style={{ width: screenSize - 120 + "px", margin: "auto" }}>
              <Table
                className="customTable"
                columns={columns?.map((col) => {
                  const { key } = col;
                  if (key == "specification") {
                    col = {
                      ...col,
                      render: (text: any, record: any) => {
                        if (record?.key == "lastRow") {
                          return <b className="justify-end">Total Qty: </b>;
                        } else {
                          return text;
                        }
                      },
                      onCell: (record: any) => {
                        if (record?.key == "lastRow") {
                          return {
                            colSpan: 5,
                          };
                        }
                        return {};
                      },
                    };
                  } else if (key == "quantity") {
                    col = {
                      ...col,
                      render: (text: any, record: any, index: number) => {
                        if (record?.key == "lastRow") {
                          const total = fields.reduce((a: any, b: any) => {
                            return +a + +b?.quantity;
                          }, 0);
                          return (
                            <div className="justify-end">
                              {Number(total).toFixed(2)}
                            </div>
                          );
                        }
                        return (
                          <div className="justify-end">
                            {Number(text).toFixed(2)}{" "}
                            {record?.uom || record?.uomLabel}
                          </div>
                        );
                      },
                    };
                  } else {
                    col = {
                      ...col,
                      onCell: (record: any) => {
                        if (record?.key == "lastRow") {
                          return {
                            colSpan: 0,
                          };
                        }
                        return {};
                      },
                    };
                  }
                  return col;
                })}
                dataSource={
                  fields?.length > 0
                    ? [
                        ...fields,
                        {
                          key: "lastRow",
                          materialName: "",
                          quantity: 0,
                          unit: 0,
                          lineTotal: 0,
                        },
                      ]
                    : []
                }
                scroll={{ x: "max-content" }}
                rowClassName={(record: any) => {
                  return record?.errorFlag == true ? "error-tableRow" : "";
                }}
                locale={{
                  emptyText: (
                    <span
                      style={{
                        height: "300px",
                        color: "#333",
                        fontSize: "16px",
                      }}
                    >
                      Please click on "Add New Item" or "Import" to add purchase
                      Request lines.
                    </span>
                  ),
                }}
                bordered
                size="small"
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100', '200'],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} materials`,
                }}
              />
            </div>

            <Row
              style={{
                margin: "20px",
              }}
            >
              <Col className="justify-between">
                <a onClick={addNewRow} className="addNewItem">
                  <Icon name="plus-round" />
                  <span>Add New Item</span>
                </a>
                {exceededMaterials.length > 0 && (
                  <div style={{ marginLeft: "auto", textAlign: "right" }}>
                    <div
                      className="mt-2"
                      style={{
                        color: "#cf1322",
                        fontSize: "13px",
                        fontWeight: 500,
                      }}
                    >
                      {exceededMaterials.map((item, idx) => (
                        <div key={idx} style={{ marginBottom: "4px" }}>
                          <b>{item.label}</b>: Entered Qty ({item.enteredQty}) exceeds PR Remaining Qty ({item.remainingQty})
                        </div>
                      ))}
                      {canBypassApproval
                        ? " You are authorized to proceed."
                        : (!isApprovalRequiredYes || !hasApprovalsSet)
                          ? " Approval is required to proceed."
                          : ""}
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </div>
          <div className="actions clearfix">
            <ul>
              <li>
                <Button
                  color="primary"
                  type="submit"
                  disabled={
                    fields?.length == 0 ||
                    fields?.find((line: any) => line?.errorFlag)?.errorFlag
                  }
                  onClick={() => {
                    if (shouldBlockNext) {
                      const materialsHtml = exceededMaterials
                        .map(
                          (item) =>
                            `<b>${item.label}</b>: Entered Qty (${item.enteredQty}) exceeds PR Remaining Qty (${item.remainingQty})`
                        )
                        .join("<br/>");

                      const extraMessage =
                        approvalRequired === "true"
                          ? " You need to set the approvals to proceed."
                          : " Approval is required to proceed.";

                      Swal.fire({
                        icon: "warning",
                        title: "PR Quantity Exceeded",
                        html: `${materialsHtml}<br/><br/>${extraMessage}`,
                      });
                      return;
                    }
                    props.next();
                  }}
                >
                  Next
                </Button>
              </li>
              <li>
                <Button
                  color="primary"
                  onClick={props.prev}
                  className="btn-dim btn btn-secondary"
                >
                  Previous
                </Button>
              </li>
            </ul>
          </div>
        </form>
      </Block>

      {activeMaterial.action && (
        <MaterialModal
          modal={!!activeMaterial.action}
          materialCategoryOptions={materialCategoryOptions}
          closeModal={() => {
            setActiveMaterial({
              action: null,
              index: null,
              item: null,
            });
          }}
          append={(data: any) => {
            // Hierarchy-aware duplicate check
            const duplicateResult = checkHierarchyDuplicate(data, fields);
            if (duplicateResult.isDuplicate) {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: duplicateResult.message,
              });
              return;
            }

            // Validate hierarchy fields before appending
            const currentWorkLevelSetting =
              workLevelSettingWatch || getValues("workLevelSetting");
            if (currentWorkLevelSetting && currentWorkLevelSetting !== "NONE") {
              const validation = validateHierarchyFields(
                data,
                currentWorkLevelSetting,
              );
              data.errorFlag = !validation.isValid;
              data.errorMessage = validation.errors;
            } else {
              data.errorFlag = false;
              data.errorMessage = [];
            }

            // Validate grouped site structures (quantity sums)
            const allItemsAfterAdd = [...fields, data];
            const grouped = groupMaterialsBySiteStructure(allItemsAfterAdd);
            const groupValidation:any = validateSiteStructureGrouping(grouped);

            if (!groupValidation.isValid) {
              Swal.fire({
                icon: "error",
                title: "Validation Error",
                html: groupValidation.errors.join("<br/>"),
              });
              return;
            }

            append(data);
          }}
          update={(index: any, data: any) => {
            const ItemsList = [...fields];

            // Hierarchy-aware duplicate check excluding the current item
            const duplicateResult = checkHierarchyDuplicate(data, ItemsList, index);
            if (duplicateResult.isDuplicate) {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: duplicateResult.message,
              });
              return;
            }

            // Validate hierarchy fields before updating
            const currentWorkLevelSetting =
              workLevelSettingWatch || getValues("workLevelSetting");
            if (currentWorkLevelSetting && currentWorkLevelSetting !== "NONE") {
              const validation = validateHierarchyFields(
                data,
                currentWorkLevelSetting,
              );
              data.errorFlag = !validation.isValid;
              data.errorMessage = validation.errors;
            } else {
              data.errorFlag = false;
              data.errorMessage = [];
            }

            // Validate grouped site structures (quantity sums)
            const allItemsAfterUpdate = ItemsList.map((item, idx) =>
              idx === index ? data : item
            );
            const grouped = groupMaterialsBySiteStructure(allItemsAfterUpdate);
            const groupValidation: any = validateSiteStructureGrouping(grouped);

            if (!groupValidation.isValid) {
              Swal.fire({
                icon: "error",
                title: "Validation Error",
                html: groupValidation.errors.join("<br/>"),
              });
              return;
            }

            update(index, data);
          }}
          formData={activeMaterial}
          materialsList={fields}
          editIndex={activeMaterial?.index ?? null}
        />
      )}
    </>
  );
};

export default PurchaseRequest_Materails;
